import React, { useEffect, useState } from "react";
import { fetchWeatherApi } from "openmeteo";
import { useTranslation } from 'react-i18next';
import './WeatherWidget.css';

const WeatherWidget = ({ lat, lng }) => {
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language?.startsWith('en') ? 'en-US' : 'tr-TR';

    const [weather, setWeather] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!lat || !lng) return;

        const fetchWeather = async () => {
            try {
                setLoading(true);
                setError(null);

                const params = {
                    latitude: lat,
                    longitude: lng,
                    current: ["temperature_2m", "relative_humidity_2m", "weather_code", "wind_speed_10m"],
                    daily: ["temperature_2m_max", "temperature_2m_min", "precipitation_sum"],
                    timezone: "Europe/Istanbul",
                    forecast_days: 7
                };

                const url = "https://api.open-meteo.com/v1/forecast";
                const responses = await fetchWeatherApi(url, params);
                const response = responses[0];

                const current = response.current();
                const daily = response.daily();
                const utcOffsetSeconds = response.utcOffsetSeconds();

                // Current data
                const currentData = {
                    temperature_2m: current.variables(0).value(),
                    relative_humidity_2m: current.variables(1).value(),
                    weather_code: current.variables(2).value(),
                    wind_speed_10m: current.variables(3).value(),
                };

                // Daily data
                const dailyTimes = Array.from(
                    { length: (Number(daily.timeEnd()) - Number(daily.time())) / daily.interval() },
                    (_, i) => new Date((Number(daily.time()) + i * daily.interval() + utcOffsetSeconds) * 1000)
                );

                const dailyData = {
                    time: dailyTimes,
                    temperature_2m_max: daily.variables(0).valuesArray(),
                    temperature_2m_min: daily.variables(1).valuesArray(),
                    precipitation_sum: daily.variables(2).valuesArray(),
                };

                setWeather({
                    current: currentData,
                    daily: dailyData
                });

                setLoading(false);
            } catch (err) {
                console.error("Weather error:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchWeather();
    }, [lat, lng]);

    const getWeatherDescription = (code) => {
        const weatherCodes = {
            0: `☀️ ${t('weather.clear')}`,
            1: `🌤️ ${t('weather.partly_cloudy')}`,
            2: `⛅ ${t('weather.cloudy')}`,
            3: `☁️ ${t('weather.overcast')}`,
            45: `🌫️ ${t('weather.fog')}`,
            48: `🌫️ ${t('weather.freezing_fog')}`,
            51: `🌦️ ${t('weather.light_drizzle')}`,
            61: `🌧️ ${t('weather.light_rain')}`,
            63: `🌧️ ${t('weather.rain')}`,
            65: `🌧️ ${t('weather.heavy_rain')}`,
            71: `🌨️ ${t('weather.light_snow')}`,
            73: `🌨️ ${t('weather.snow')}`,
            75: `🌨️ ${t('weather.heavy_snow')}`,
            95: `⛈️ ${t('weather.storm')}`,
        };
        return weatherCodes[code] || `🌤️ ${t('weather.unknown')}`;
    };

    if (loading) return <div className="weather-skeleton">🌤️ {t('weather.loading')}</div>;
    if (error) return <div className="weather-error">❌ {error}</div>;
    if (!weather) return null;

    return (
        <div className="weather-widget">
            <div className="current-weather">
                <div className="current-main">
                    <div className="temp">{weather.current.temperature_2m.toFixed(1)}°C</div>
                    <div className="desc">{getWeatherDescription(weather.current.weather_code)}</div>
                </div>
                <div className="current-details">
                    <div className="detail">💧 {t('weather.humidity')}: %{weather.current.relative_humidity_2m.toFixed(0)}</div>
                    <div className="detail">💨 {t('weather.wind')}: {weather.current.wind_speed_10m.toFixed(1)} km/h</div>
                </div>
            </div>

            <div className="forecast-mini">
                {weather.daily.time.slice(1, 5).map((date, i) => (
                    <div key={i} className="forecast-item">
                        <span className="day">{date.toLocaleDateString(currentLocale, { weekday: "short" })}</span>
                        <span className="temp-range">{weather.daily.temperature_2m_max[i + 1].toFixed(0)}°</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WeatherWidget;
